import { useDispatch, useSelector } from 'react-redux';
import { FileImage, Loader2, Upload, X } from 'lucide-react';
import { ChangeEvent, useEffect, useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { toast } from 'sonner';
import CreatableSelect from 'react-select/creatable';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { usePostUploadForm } from '@/redux/post/usePostForm';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import ErrorMessage from '@/components/ErrorMessage';
import { DropZone } from '@/components/ui/drop-zone';
import { Button } from '@/components/ui/button';
import { thumbnailExtensions } from '@/redux/post/post.schema';
import { IDifficulty } from '@/redux/post/post.d';
import {
  removeFileFromUploadQueue,
  addFileToUploadQueue,
  selectProjectEditorData,
  setProjectEditorData,
  removeFileFromUploadQueueByCategory,
  addTagToProjectEditorData,
} from '@/redux/post/post.slice';
import {
  AttachmentParentType,
  AttachmentType,
  IAttachmentCategory,
} from '@/redux/attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';

// Wizard step form: the branching is one guard per optional field, and it has no
// test coverage, so CRAP trips on a plain cyclomatic-7 component.
// fallow-ignore-next-line complexity
function BasicDetails({ errors }: { errors: { [key: string]: string } }) {
  const projectEditorData = useSelector(selectProjectEditorData);
  const uploadPostForm = usePostUploadForm(projectEditorData);
  const { data: categories, isLoading: isLoadingPostCategories } =
    useGetCategoriesQuery({ type: ICategoryType.POST });

  const {
    data: postTags,
    refetch: refetchTags,
    isLoading: isTagsLoading,
  } = useGetTagsQuery({
    type: ITagType.POST,
  });

  const [createTag] = useCreateTagMutation();
  const [deleteAttachment, { isLoading: isDeleteAttachmentLoading }] =
    useDeleteAttachmentMutation();
  const [preview, setPreview] = useState<string>();
  const dispatch = useDispatch();

  const handleCategoryChange = ({ value }: { value?: string | undefined }) => {
    if (value) {
      dispatch(setProjectEditorData({ ...projectEditorData, category: value }));
    }
  };

  const handleDifficultyChange = ({
    value,
  }: {
    value?: string | undefined;
  }) => {
    if (value) {
      dispatch(
        setProjectEditorData({ ...projectEditorData, difficulty: value })
      );
    }
  };

  const getDifficultyOptions = () =>
    Object.values(IDifficulty).map((level) => ({ label: level, value: level }));

  const getPostCategoryOptions = (): {
    label: string;
    value: string;
  }[] => {
    const options = categories?.map((category) => ({
      label: category.name,
      value: category._id as string,
    }));

    return options || [];
  };

  const getTagOptions = () => {
    const options = postTags?.map((tag) => ({
      label: tag.name,
      value: tag._id as string,
    }));
    return options;
  };

  const handleCreateTag = async (option: string) => {
    try {
      const newTag = await createTag({
        name: option,
        type: ITagType.POST,
      });

      if (newTag?.error) {
        // refactor this
        const { data } = newTag.error as {
          data: {
            message: string;
          };
        };
        throw new Error(data.message);
      }

      await refetchTags();
      dispatch(addTagToProjectEditorData(newTag.data?._id as string));
      toast.success('Tag created successfully');
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error ? e.message : 'Failed to create tag';
      toast.error(errorMessage);
    }
  };

  const handleTagChange = (
    options: MultiValue<{ label: string; value: string }>
  ) => {
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        tags: options?.map((option) => option.value),
      })
    );
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    dispatch(setProjectEditorData({ ...projectEditorData, [name]: value }));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      // check for the same type of file in the fileUploadQueue and if it exists remove it
      const file = event.target.files[0];

      // Validate file extension
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf('.'));
      if (!thumbnailExtensions.includes(fileExtension)) {
        toast.error('Invalid file type', {
          description: 'Thumbnail must be a .png, .jpg, or .jpeg file',
        });
        // Clear the file input
        const input = event.target;
        input.value = '';
        return;
      }

      const fileHash = await hashFile(file);

      dispatch(
        removeFileFromUploadQueueByCategory(IAttachmentCategory.postThumbnail)
      );

      dispatch(
        addFileToUploadQueue({
          parentId: projectEditorData._id,
          parentType: AttachmentParentType.POST,
          category: IAttachmentCategory.postThumbnail,
          resourceType: AttachmentType.image,
          fileName: file.name,
          fileHash: fileHash.toString(),
          fileSize: file.size,
          preview: URL.createObjectURL(file),
        })
      );
    }
  };

  const handleRemoveFile = async () => {
    setPreview(undefined);
    const thumbnail = projectEditorData.fileUploadQueue.find(
      (file) => file.category === IAttachmentCategory.postThumbnail
    );
    dispatch(
      removeFileFromUploadQueue({
        fileHash: thumbnail?.fileHash,
        category: IAttachmentCategory.postThumbnail,
      })
    );

    // If the thumbnail exists in server, delete it
    if (!projectEditorData?.thumbnail?._id) return;
    try {
      await deleteAttachment({
        attachmentId: projectEditorData?.thumbnail?._id,
      });
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          thumbnail: null,
        })
      );
    } catch {
      toast.error('Failed to delete thumbnail');
    }
  };

  const uploadQueueThumbnail = projectEditorData.fileUploadQueue.find(
    (file) => file.category === IAttachmentCategory.postThumbnail
  );

  const getThumbnailName = (): string => {
    if (uploadQueueThumbnail) {
      return uploadQueueThumbnail?.fileName;
    }

    return projectEditorData?.thumbnail?.fileName || '';
  };

  const thumbnail = projectEditorData.thumbnail || uploadQueueThumbnail;

  // The thumbnail image, whether it was just picked (a local blob URL still in
  // the upload queue) or already saved (a signed URL on the post).
  const thumbnailSrc =
    projectEditorData.thumbnail?.url ?? uploadQueueThumbnail?.preview;

  // Show it in the preview as soon as one exists — on pick, and on load when
  // editing a post that already has one. Only assigns on a truthy src: while a
  // save is in flight the upload queue is emptied before the saved attachment
  // comes back, and blanking the preview in that gap makes the image flicker
  // out. Clearing is left to "Close Preview" / remove, which are explicit.
  useEffect(() => {
    if (thumbnailSrc) {
      setPreview(thumbnailSrc);
    }
  }, [thumbnailSrc]);

  return (
    <div className="h-full flex flex-col font-tertiary">
      <Card className="h-full w-full max-w-6xl mx-auto flex flex-col">
        <CardContent className="p-6 overflow-y-auto flex-1">
          <Form {...uploadPostForm}>
            <form>
              <div className="space-y-4">
                <FormField
                  control={uploadPostForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium mb-2">
                        Project Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter title"
                          onChange={handleInputChange}
                          value={projectEditorData.title}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.title} />
                    </FormItem>
                  )}
                />

                <FormField
                  control={uploadPostForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium mb-2">
                        Description
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter description"
                          onChange={handleInputChange}
                          value={projectEditorData.description}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.description} />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={uploadPostForm.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Category
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={getPostCategoryOptions()}
                            onChange={(newValue) =>
                              handleCategoryChange({
                                ...newValue,
                              } as unknown as { value: string })
                            }
                            isLoading={isLoadingPostCategories}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.category
                                ? {
                                    label:
                                      categories?.find(
                                        (cat) =>
                                          cat._id === projectEditorData.category
                                      )?.name || '',
                                    value: projectEditorData.category,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.category} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={uploadPostForm.control}
                    name="difficulty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Project Difficulty
                        </FormLabel>
                        <FormControl>
                          <Select
                            options={getDifficultyOptions()}
                            onChange={(newValue) =>
                              handleDifficultyChange({
                                ...newValue,
                              } as unknown as { value: string })
                            }
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.difficulty
                                ? {
                                    label: projectEditorData.difficulty,
                                    value: projectEditorData.difficulty,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.difficulty} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={uploadPostForm.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium mb-2">
                          Tags
                        </FormLabel>
                        <FormControl>
                          <CreatableSelect
                            isMulti
                            options={getTagOptions()}
                            isLoading={isTagsLoading}
                            name={field.name}
                            onCreateOption={handleCreateTag}
                            onChange={(newValue) => {
                              if (newValue) {
                                const validOptions = newValue.filter(
                                  (
                                    option
                                  ): option is {
                                    label: string;
                                    value: string;
                                  } =>
                                    option !== null &&
                                    option.value !== undefined
                                );
                                handleTagChange(validOptions);
                              }
                            }}
                            value={projectEditorData?.tags
                              .map((tagId) => {
                                const tag = postTags?.find(
                                  (t) => t._id === tagId
                                );
                                return tag
                                  ? { label: tag.name, value: tag._id }
                                  : null;
                              })
                              .filter(Boolean)}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.tags} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-3">
                  <FormLabel className="text-sm font-medium">
                    Select a Picture as your Thumbnail for the project
                  </FormLabel>
                  <DropZone
                    onDrop={handleFileChange}
                    preview={preview}
                    setPreview={setPreview}
                  >
                    <Upload className="mx-auto h-4 w-10 text-muted-foreground" />
                    <div className="mt-4">
                      <p className="text-sm text-muted-foreground">
                        Select a file or drag and drop here
                      </p>
                    </div>
                    <Input
                      type="file"
                      className="hidden"
                      onChange={handleFileChange}
                      accept={thumbnailExtensions.join(',')}
                      id="file-upload"
                    />
                    <Button
                      variant="outline"
                      type="button"
                      className="mt-4 border-gray-400 border-2"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById('file-upload')?.click();
                      }}
                    >
                      Select file
                    </Button>
                    <ErrorMessage error={errors.thumbnail} />
                  </DropZone>

                  {thumbnail && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium">Uploaded Files</h3>
                      <div className="flex flex-col gap-2">
                        <div className="space-y-2 max-w-4xl">
                          <div
                            className="flex items-center gap-2 py-2 px-3 border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (projectEditorData?.thumbnail) {
                                setPreview(projectEditorData.thumbnail?.url);
                              } else if (uploadQueueThumbnail) {
                                setPreview(uploadQueueThumbnail?.preview);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                if (projectEditorData?.thumbnail) {
                                  setPreview(projectEditorData.thumbnail?.url);
                                } else if (uploadQueueThumbnail) {
                                  setPreview(uploadQueueThumbnail?.preview);
                                }
                              }
                            }}
                          >
                            <FileImage className="text-[#0F91D2] h-4 w-4 flex-shrink-0" />
                            <div className="min-w-0 flex-1 break-all text-sm font-medium line-clamp-2">
                              {getThumbnailName()}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              type="button"
                              className="h-6 w-6 p-0 rounded-full"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFile();
                              }}
                            >
                              {isDeleteAttachmentLoading ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <X className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default BasicDetails;
